<template>
    <div class="dashboard-page">
        <div class="dashboard-content">
            <div class="row gy-4">
                <div class="col-lg-3">
                    <PageCard class="welcome-card">
                        <div>
                            <div class="greeting-section">
                                <div class="greeting-icon">
                                    <i class="fas fa-sun"></i>
                                </div>
                                <div class="greeting-info">
                                    <span class="time-text">{{ formattedTime }}</span>
                                    <span class="realtime-label">Realtime Insight</span>
                                </div>
                            </div>
                            <div v-if="tenant" class="tenant-name">
                                {{ tenant.name || tenant.subdomain }}
                            </div>
                            <div class="date-display">
                                <span class="date-label">Today</span>
                                <span class="date-value">{{ formattedDate }}</span>
                            </div>
                            <div class="mt-3">
                                <router-link to="/attendance-records">
                                    <Button noMargin class="attendance-btn">
                                        <i class="fas fa-calendar-check me-2"></i>View Attendance
                                    </Button>
                                </router-link>
                            </div>
                        </div>
                    </PageCard>
                </div>
                <div class="col-lg-9">
                    <DashboardReportNumbers />
                </div>
                <div class="col-lg-12">
                    <DashboardCostApprovals />
                </div>
                <div class="col-lg-12">
                    <DashboardTaskOverview />
                </div>
                <div class="col-lg-8">
                    <DashboardAttendanceComparison />
                </div>
                <div class="col-lg-4">
                    <DashboardCalendarReport />
                </div>
                <div class="col-lg-12">
                    <DashboardPerformanceStats />
                </div>
                <div class="col-lg-12">
                    <DashboardLeaveRequestApproval />
                </div>
            </div>

            <div class="asset-section">
                <div class="asset-section__header">
                    <div class="asset-section__icon"><i class="fas fa-boxes"></i></div>
                    <div>
                        <h3 class="asset-section__title">Assets Overview</h3>
                        <p class="asset-section__sub">Track allocated &amp; available assets</p>
                    </div>
                </div>
            </div>
            <Spinner v-if="!dashboardStatsAssetData" />
            <div class="asset-cards" v-if="dashboardStatsAssetData">
                <DashboardAssetInfoCard
                    title="Total"
                    :link="`/assets?type=${ASSET_FILTER_TYPES.ALL}`"
                    :assetCount="dashboardStatsAssetData.totalAssetCount"
                    :assetAmount="dashboardStatsAssetData.totalAssetAmount"
                />
                <DashboardAssetInfoCard
                    title="Allocated"
                    :link="`/asset-allocations`"
                    :assetCount="dashboardStatsAssetData.totalAllocatedAssetCount"
                    :assetAmount="dashboardStatsAssetData.totalAllocatedAssetAmount"
                />
                <DashboardAssetInfoCard
                    title="Non-Allocated"
                    :link="`/assets?type=${ASSET_FILTER_TYPES.NOT_ALLOCATED}`"
                    :assetCount="dashboardStatsAssetData.totalNonAllocatedAssetCount"
                    :assetAmount="dashboardStatsAssetData.totalNonAllocatedAssetAmount"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { http } from "../utils/http"
import { initSocket } from "../utils/socket"
import millify from "millify"
import { mapState, mapActions } from "vuex"
import DashboardReportViewCard from "../components/reports/DashboardReportViewCard.vue"
import DashboardReportNumbers from "./dashboard/DashboardReportNumbers.vue"
import DashboardChart from "./dashboard/DashboardChart.vue"
import PageCard from "../components/layout/PageCard.vue"
import Button from "../components/UI/Button.vue"
import { humanReadableDate } from "../utils/common"
import SectionTitle from "../components/UI/SectionTitle.vue"
import DashboardAssetInfoCard from "../components/reports/DashboardAssetInfoCard.vue"
import DashboardTaskOverview from "../components/reports/DashboardTaskOverview.vue"
import DashboardLeaveRequestApproval from "../components/reports/DashboardLeaveRequestApproval.vue"
import DashboardCalendarReport from "../components/reports/DashboardCalendarReport.vue"
import DashboardAttendanceComparison from "../components/reports/DashboardAttendanceComparison.vue"
import DepartmentwiseAttendance from "../components/reports/DepartmentwiseAttendance.vue"
import DashboardPerformanceStats from "../components/reports/DashboardPerformanceStats.vue"
import DashboardCostApprovals from "../components/reports/DashboardCostApprovals.vue"
import Spinner from "../components/UI/Spinner.vue"
import { ASSET_FILTER_TYPES } from "../utils/constants"

export default {
    data() {
        return {
            initialized: false,
            loading: true,
            formattedTime: "",
            formattedDate: "",
            ASSET_FILTER_TYPES,
        }
    },
    async mounted() {
        this.updateFormattedTime()
        this.updateFormattedDate()
        
        // Initialize socket with tenant context
        initSocket()

        await this.fetchDashboardStats()
        await this.fetchDashboardStatsAttendance()
        await this.fetchDashboardStatsPerformance()
        await this.fetchDashboardStatsAssetData()
        await this.fetchDashboardStatsLeaveRequests()
        await this.fetchDashboardStatsAttendanceDepartmentWise()

        setInterval(() => {
            this.updateFormattedTime()
            this.updateFormattedDate()
        }, 1000)
    },
    computed: {
        ...mapState("misc", ["dashboardStats", "dashboardStatsAssetData"]),
        ...mapState("auth", ["tenant"]),
    },
    methods: {
        ...mapActions("misc", [
            "fetchDashboardStats",
            "fetchDashboardStatsAssetData",
            "fetchDashboardStatsAttendance",
            "fetchDashboardStatsAttendanceDepartmentWise",
            "fetchDashboardStatsPerformance",
            "fetchDashboardStatsLeaveRequests",
        ]),
        millify,
        updateFormattedTime() {
            this.formattedTime = new Date().toLocaleTimeString()
        },
        updateFormattedDate() {
            this.formattedDate = humanReadableDate(new Date())
        },
    },
    components: {
        SectionTitle,
        DashboardChart,
        Button,
        DashboardAttendanceComparison,
        DepartmentwiseAttendance,
        DashboardReportViewCard,
        DashboardReportNumbers,
        PageCard,
        DashboardAssetInfoCard,
        DashboardTaskOverview,
        DashboardLeaveRequestApproval,
        DashboardCalendarReport,
        DashboardPerformanceStats,
        DashboardCostApprovals,
        Spinner,
    },
}
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.dashboard-page {
    animation: fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
@keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
}
.dashboard-content {
    min-height: 300px;
}
.welcome-card {
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, $primaryColor, lighten($primaryColor, 20%));
        border-radius: 16px 16px 0 0;
    }
}
.greeting-section {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 4px;
}
.greeting-icon {
    width: 40px;
    height: 40px;
    border-radius: $radiusSm;
    background: linear-gradient(135deg, #fbbf24, #f59e0b);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 18px;
    flex-shrink: 0;
}
.greeting-info {
    display: flex;
    flex-direction: column;
}
.time-text {
    font-size: 18px;
    font-weight: 600;
    color: $gray800;
    line-height: 1.2;
}
.realtime-label {
    font-size: 11px;
    color: $gray400;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 500;
}
.tenant-name {
    font-size: 14px;
    font-weight: 600;
    color: $primaryColor;
    margin-top: 12px;
    padding: 8px 12px;
    background: $primaryLight;
    border-radius: $radiusSm;
    display: inline-block;
}
.date-display {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.date-label {
    font-size: 12px;
    color: $gray400;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-weight: 500;
}
.date-value {
    font-size: 14px;
    font-weight: 500;
    color: $gray700;
}
.attendance-btn {
    width: 100% !important;
    min-width: unset !important;
    font-size: 12px !important;
}
.asset-section {
    margin: 24px 0 14px;
}
.asset-section__header {
    display: flex; align-items: center; gap: 10px;
}
.asset-section__icon {
    width: 34px; height: 34px; border-radius: 8px;
    background: linear-gradient(135deg, #eff6ff, #dbeafe); color: #2563eb;
    display: flex; align-items: center; justify-content: center; font-size: 14px;
}
.asset-section__title {
    font-size: 15px; font-weight: 700; color: #1e293b; margin: 0; line-height: 1.2;
}
.asset-section__sub {
    font-size: 11.5px; color: #94a3b8; margin: 0;
}
.asset-cards {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
}

/* ---- Tablet ---- */
@media (max-width: 768px) {
    .greeting-section {
        gap: 10px;
    }
    .time-text {
        font-size: 16px;
    }
    .greeting-icon {
        width: 36px;
        height: 36px;
        font-size: 16px;
    }
}

/* ---- Mobile ---- */
@media (max-width: 480px) {
    .dashboard-content {
        min-height: auto;
    }
    .tenant-name {
        font-size: 12px;
        padding: 6px 10px;
    }
    .time-text {
        font-size: 15px;
    }
    .asset-section {
        margin: 16px 0 12px;
    }
    .asset-cards {
        grid-template-columns: 1fr;
    }
}
</style>
